import React from 'react'
import { Link } from '@inertiajs/react'
import { ArrowRight, Moon } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { DeltaChip, percentDelta, pointsDelta, type Delta } from '@/components/dashboard/Delta'
import { formatCount, formatMoney } from '@/lib/format'

/**
 * Band 4 — tonight, demoted. Five figures in one strip: the front-desk view an RM
 * glances at, not the view they steer by. ADR and RevPAR here are realized (revenue
 * actually on the books for tonight ÷ rooms sold / rooms available), the same
 * definition Band 1 uses at month scale.
 *
 * Year-ago context deliberately lives in Band 1: one night against one night 364
 * days ago is noise, one month against one month is a decision.
 */

export interface TodaySnapshotData {
  totalRooms: number
  bookedTonight: number
  occupancyPct: number
  avgRate: number
  revenue: number
  revpar: number
  activeOverrides: number
  currency: string
  lastWeekOccupancyPct: number
  lastWeekAvgRate: number
  lastWeekRevenue: number
  lastWeekRevpar: number
  lastYearOccupancyPct: number
  lastYearAvgRate: number
  lastYearRevenue: number
  lastYearRevpar: number
  lastYearHasData: boolean
}

interface CellProps {
  label: string
  value: string
  detail?: string
  delta?: Delta | null
}

function Cell({ label, value, detail, delta }: CellProps) {
  return (
    <div className="min-w-0 px-5 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1.5 text-xl font-bold tracking-tight text-foreground tabular-nums">{value}</p>
      <div className="mt-1.5 flex min-h-[20px] items-center gap-1.5">
        {delta && <DeltaChip delta={delta} />}
        {detail && <span className="truncate text-[11px] text-muted-foreground">{detail}</span>}
      </div>
    </div>
  )
}

export default function TonightStrip({
  snapshot,
  propertyBasePath,
}: {
  snapshot: TodaySnapshotData
  /** The property's backend-served canonical path. */
  propertyBasePath: string
}) {
  const cur = snapshot.currency
  const leftToSell = snapshot.totalRooms - snapshot.bookedTonight
  const tonight = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  // A rate comparison against a night that sold nothing has no honest percentage, so
  // the caption says so rather than leaving a bare "vs last week" with no number.
  const rateCell = (label: string, value: string, delta: ReturnType<typeof percentDelta>): CellProps => ({
    label,
    value,
    delta,
    detail: delta ? 'vs last week' : 'no last-week rate',
  })

  const cells: CellProps[] = [
    {
      label: 'Rooms sold',
      value: `${formatCount(snapshot.bookedTonight)} / ${formatCount(snapshot.totalRooms)}`,
      detail: 'tonight',
    },
    {
      label: 'Occupancy',
      value: `${snapshot.occupancyPct}%`,
      delta: pointsDelta(snapshot.occupancyPct, snapshot.lastWeekOccupancyPct),
      detail: 'vs last week',
    },
    rateCell('ADR', formatMoney(snapshot.avgRate, cur), percentDelta(snapshot.avgRate, snapshot.lastWeekAvgRate)),
    rateCell(
      'RevPAR',
      formatMoney(snapshot.revpar, cur),
      percentDelta(snapshot.revpar, snapshot.lastWeekRevpar)
    ),
    {
      label: 'Left to sell',
      value: formatCount(leftToSell),
      detail: leftToSell === 1 ? 'room unsold' : 'rooms unsold',
    },
  ]

  return (
    <DashboardCard
      title="Tonight"
      description={`${tonight} · realized revenue ${formatMoney(snapshot.revenue, cur)}`}
      icon={<Moon className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          "Tonight's realized position: rooms actually sold and the revenue on the books for this single night. ADR is that revenue divided by rooms sold, not an average of your list prices.",
      }}
      action={
        <Link
          href={`${propertyBasePath}/overrides`}
          className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-100 transition-colors hover:bg-amber-100"
        >
          {formatCount(snapshot.activeOverrides)} active override
          {snapshot.activeOverrides === 1 ? '' : 's'}
          <ArrowRight className="h-3 w-3" />
        </Link>
      }
      padded={false}
    >
      <div className="grid grid-cols-2 divide-x divide-y divide-border/40 sm:grid-cols-3 lg:grid-cols-5 lg:divide-y-0">
        {cells.map((cell) => (
          <Cell key={cell.label} {...cell} />
        ))}
      </div>
    </DashboardCard>
  )
}
